/**
 * ClientLanguageSwitcher — quick language selector for the client PWA header.
 *
 * Iter 266: gives the client autonomy to change their preferred_language
 * after it was set (either by them via the trilingual modal or by an
 * admin override). Small popover with 3 language buttons; on click:
 *  1. PUT /api/client/profile → { preferred_language: <code> }
 *  2. Update LanguageContext so the UI reacts immediately.
 *  3. Sync localStorage 'client-data' so a page reload keeps the choice.
 */
import React, { useState, useRef, useEffect } from 'react';
import { Globe, Check } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useClientAuth } from '../context/ClientAuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const LANGS = [
  { code: 'pt', flag: '🇧🇷', label: 'Português' },
  { code: 'en', flag: '🇺🇸', label: 'English' },
  { code: 'es', flag: '🇪🇸', label: 'Español' },
];

const ClientLanguageSwitcher = () => {
  const { language, changeLanguage } = useLanguage();
  const { client, token, isAuthenticated } = useClientAuth();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  if (!isAuthenticated) return null;

  const activeCode = (client?.preferred_language || language || 'pt').toLowerCase();
  const active = LANGS.find(l => l.code === activeCode) || LANGS[0];

  const pick = async (code) => {
    if (saving || code === activeCode) { setOpen(false); return; }
    setSaving(true);
    try {
      await fetch(`${API}/api/client/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ preferred_language: code }),
      });
      changeLanguage(code);
      try {
        const saved = JSON.parse(localStorage.getItem('client-data') || '{}');
        saved.preferred_language = code;
        localStorage.setItem('client-data', JSON.stringify(saved));
      } catch { /* ignore */ }
      setOpen(false);
      // Reload so all cached copy (dashboard cards, emails preview, etc.) refetches in the new lang.
      setTimeout(() => window.location.reload(), 150);
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert('Erro ao trocar idioma / Error saving language / Error al guardar idioma');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid="client-language-switcher">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium text-gray-500 hover:text-demarre-700 hover:bg-gray-50 rounded-lg whitespace-nowrap transition-colors"
        aria-label="Change language"
        title={`${active.flag} ${active.label}`}
        data-testid="client-language-switcher-button"
      >
        <Globe className="w-4 h-4" />
        <span className="hidden sm:inline">{active.flag}</span>
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50"
          role="menu"
          data-testid="client-language-switcher-menu"
        >
          {LANGS.map(l => (
            <button
              key={l.code}
              type="button"
              onClick={() => pick(l.code)}
              disabled={saving}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                l.code === activeCode ? 'bg-demarre-50 text-demarre-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'
              }`}
              role="menuitem"
              data-testid={`client-language-switcher-opt-${l.code}`}
            >
              <span className="text-lg" aria-hidden="true">{l.flag}</span>
              <span className="flex-1">{l.label}</span>
              {l.code === activeCode && <Check className="w-4 h-4 text-demarre-700" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientLanguageSwitcher;
